export interface WorkloadDossier {
  id: string;
  number: string;
  title: string;
  stage: string;
  role: string;
  next_hearing_at: string | null;
}

/** Forme brute renvoyée par la RPC get_lawyer_workload. */
export interface RawWorkload {
  visible: boolean;
  dossiers?: WorkloadDossier[];
  open_tasks?: number;
  next_hearing_at?: string | null;
  next_task_due?: string | null;
}

export interface Workload {
  visible: boolean;
  dossiers: WorkloadDossier[];
  activeDossiersCount: number;
  openTasksCount: number;
  /** Plus proche échéance entre prochaine audience et prochaine tâche, ou null. */
  nextDeadline: string | null;
}

/** Fonction pure : agrège la charge à partir de la réponse RPC. */
export function computeWorkload(raw: RawWorkload): Workload {
  const dossiers = raw.dossiers ?? [];
  const candidates = [raw.next_hearing_at, raw.next_task_due].filter(
    (d): d is string => typeof d === 'string' && d.length > 0,
  );
  const nextDeadline =
    candidates.length === 0
      ? null
      : candidates.reduce((min, d) => (d < min ? d : min));

  return {
    visible: raw.visible,
    dossiers,
    activeDossiersCount: dossiers.length,
    openTasksCount: raw.open_tasks ?? 0,
    nextDeadline,
  };
}

/** Appelle la RPC et agrège. Renvoie null si le collaborateur est introuvable. */
export async function getLawyerWorkload(targetId: string): Promise<Workload | null> {
  const { supabase } = await import('../../lib/supabaseClient');
  const { data, error } = await supabase.rpc('get_lawyer_workload', { p_target_id: targetId });
  if (error) throw error;
  if (data == null) return null;
  return computeWorkload(data as unknown as RawWorkload);
}

export interface PublicProfileUpdate {
  bio: string | null;
  expertise: string[];
  show_dossiers: boolean;
}

/**
 * Met à jour le profil public du collaborateur courant (soi-même).
 * Passe par une RPC SECURITY DEFINER qui ne whiteliste que bio/expertise/show_dossiers :
 * un UPDATE direct sur `collaborators` est interdit côté RLS (sinon escalade de privilèges
 * possible en modifiant la colonne `role`). Le paramètre `collaboratorId` n'est plus utilisé
 * pour cibler la ligne (la RPC cible auth.uid()) mais conservé pour la signature appelante.
 */
export async function updatePublicProfile(
  _collaboratorId: string,
  update: PublicProfileUpdate,
): Promise<void> {
  const { supabase } = await import('../../lib/supabaseClient');
  const { error } = await supabase.rpc('update_my_public_profile', {
    // La fonction SQL accepte un p_bio NULL (effacer la bio), mais le générateur
    // de types Supabase l'infère non-null faute de DEFAULT. Cast volontaire.
    p_bio: update.bio as string,
    p_expertise: update.expertise,
    p_show_dossiers: update.show_dossiers,
  });
  if (error) throw error;
}
